import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { FormsModule, ReactiveFormsModule, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../constants/constants';
import { ReviewService } from '../../../services/review.service';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CommonDataService } from '../../../services/common-data.service';
import { Subject, takeUntil } from 'rxjs';
import { SiFormFieldComponent } from '../../Si-Web-Toolkit/si-form-field/si-form-field.component';
import { SiDropDownComponent } from '../../Si-Web-Toolkit/si-drop-down/si-drop-down.component';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { SiTextAreaComponent } from '../../Si-Web-Toolkit/si-text-area/si-text-area.component';
import { IDampersForm } from '../../../models/SolChecklistInterfaces/IDampersForm';
import { ISmokeDampersForm } from '../../../models/SolChecklistInterfaces/ISmokeDampersForm';
import { IMeteringForm } from '../../../models/SolChecklistInterfaces/IMeteringForm';
import { IIntegrationForm } from '../../../models/SolChecklistInterfaces/IIntegrationForm';
import { ISystemInterfacesForm } from '../../../models/SolChecklistInterfaces/ISystemInterfacesForm';
import { IExecutionScheduleForm } from '../../../models/SolChecklistInterfaces/IExecutionScheduleForm';
import { IOutsidePurchaseForm } from '../../../models/SolChecklistInterfaces/IOutsidePurchaseForm';
import { IWiringResponsibilitiesForm } from '../../../models/SolChecklistInterfaces/IWiringResponsibilitiesForm';
import { IManagementStationForm } from '../../../models/SolChecklistInterfaces/IManagementStationForm';
import { IElectricalWiringForm } from '../../../models/SolChecklistInterfaces/IElectricalWiringForm';
import { IWiringInterlockForm } from '../../../models/SolChecklistInterfaces/IWiringInterlockForm';
import { SiCurrencyFieldComponent } from '../../Si-Web-Toolkit/si-currency-field/si-currency-field.component';
import { CostReviewDampers } from '../../../models/CostReviewDampers';
import { CostReviewSmokeDampers } from '../../../models/CostReviewSmokeDampers';
import { CostReviewMetering } from '../../../models/CostReviewMetering';
import { CostReviewAttributes } from '../../../models/CostReviewAttributes';
import { CostReviewSecurityEnUsAttributes } from '../../../models/CostReviewSecurityEnUsAttributes';
import { CostReviewFireEnUsAttributes } from '../../../models/CostReviewFireEnUsAttributes';
import { CostReviewElectricalWiring } from '../../../models/CostReviewElectricalWiring';
import { CostReviewFireEnUsExecutionSchedule } from '../../../models/CostReviewFireEnUsExecutionSchedule';
import { CostReviewFireEnUsOutsidePurchases } from '../../../models/CostReviewFireEnUsOutsidePurchases';
import { CostReviewFireEnUsSystemInterfaces } from '../../../models/CostReviewFireEnUsSystemInterfaces';
import { CostReviewFireEnUsWiringResponsibilities } from '../../../models/CostReviewFireEnUsWiringResponsibilities';
import { CostReviewIntegration } from '../../../models/CostReviewIntegration';
import { CostReviewSecurityEnUsExecutionSchedule } from '../../../models/CostReviewSecurityEnUsExecutionSchedule';
import { CostReviewSecurityEnUsManagementStation } from '../../../models/CostReviewSecurityEnUsManagementStation';
import { CostReviewWiringInterlocks } from '../../../models/CostReviewWiringInterlocks';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';

import { SiCheckBoxComponent } from '../../Si-Web-Toolkit/si-check-box/si-check-box.component';
import { SiCheckBoxGroupComponent } from '../../Si-Web-Toolkit/si-check-box-group/si-check-box-group.component';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { CostReviewEstimateType } from '../../../public-api';
import { SiComboBoxComponent } from '../../Si-Web-Toolkit/si-combo-box/si-combo-box.component';

type FireExecutionScheduleFormControls = Pick<
  IExecutionScheduleForm,
  | 'phasedInstallationOccupancy'
  | 'projectSchedulePublished'
  | 'firePlanIdentified'
  | 'localOfficeTechnicalCompetencies'
  | 'localOfficeStaffedForWork'
  | 'specialCommissioningProceduresRequired'
  | 'designConsiderations'
  | 'unionProject'
  | 'specialTaggingRequirements'
  | 'responsibleForHazardousMaterials'
  | 'confinedWorkSpace'
  | 'responsibleForInaccessibleAccess'
>;

type DampersFormValue = ReturnType<FormGroup<IDampersForm>['getRawValue']>;
type SmokeDampersFormValue = ReturnType<FormGroup<ISmokeDampersForm>['getRawValue']>;
type MeteringFormValue = ReturnType<FormGroup<IMeteringForm>['getRawValue']>;
type IntegrationFormValue = ReturnType<FormGroup<IIntegrationForm>['getRawValue']>;
type SystemInterfacesFormValue = ReturnType<FormGroup<ISystemInterfacesForm>['getRawValue']>;
type FireExecutionScheduleFormValue = ReturnType<FormGroup<FireExecutionScheduleFormControls>['getRawValue']>;
type OutsidePurchaseFormValue = ReturnType<FormGroup<IOutsidePurchaseForm>['getRawValue']>;
type WiringResponsibilitiesFormValue = ReturnType<FormGroup<IWiringResponsibilitiesForm>['getRawValue']>;
type ManagementStationFormValue = ReturnType<FormGroup<IManagementStationForm>['getRawValue']>;
type ElectricalWiringFormValue = ReturnType<FormGroup<IElectricalWiringForm>['getRawValue']>;
type WiringInterlockFormValue = ReturnType<FormGroup<IWiringInterlockForm>['getRawValue']>;

@Component({
  selector: 'lib-sol-checklist-part-three',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatLabel,
    MatFormFieldModule,
    MatRadioModule,
    MatInputModule,
    SiFormFieldComponent,
    SiDropDownComponent,
    SiRadioButtonComponent,
    SiTextAreaComponent,
    SiCurrencyFieldComponent,
    TrackChangesDirective,
    SiCheckBoxComponent,
    SiCheckBoxGroupComponent,
    SiComboBoxComponent,
  ],
  templateUrl: './sol-checklist-part-three.component.html',
  styleUrl: './sol-checklist-part-three.component.scss'
})
export class SolChecklistPartThreeComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  constants = Constants;
  business = '';
  isSalePriceGreaterThan150 = false;
  isReadMode = false;
  isDisabled = false;
  sectionToggleState: Record<string, boolean> = {};
  sectionDisableState: Record<string, boolean> = {};
  isFireSmokeDampersDisabled = false;
  isLedAnnunciatorDisabled = false;
  isAlphaNumericAnnunciatorDisabled = false;
  isOtherNotesDisabled = false;
  managementStationList: {label: string, value: string}[] = [];
  optionalWorkflowCondition = false;

  // Checkbox group options arrays
  coolingTowerWiringOptions = ['COOLING_TOWER_WIRING_BASIN_HEAT', 'COOLING_TOWER_WIRING_VIBRATION'];

  // Radio options for minimum conduit size, ordered to match MINIMUM_CONDUIT_SIZE_1..8 codes.
  // Backend descriptions use unicode fraction/quote characters that never match the plain-ASCII
  // translations, so matching is done against the translation keys instead of the descriptions.
  minimumConduitSizeOptions = [
    'MINIMUM_CONDUIT_SIZE_HALF_INCH',
    'MINIMUM_CONDUIT_SIZE_THREE_QUARTER_INCH',
    'MINIMUM_CONDUIT_SIZE_ONE_INCH',
    'MINIMUM_CONDUIT_SIZE_ONE_AND_QUARTER_INCH',
    'MINIMUM_CONDUIT_SIZE_ONE_AND_HALF_INCH',
    'MINIMUM_CONDUIT_SIZE_ONE_AND_THREE_QUARTER_INCH',
    'MINIMUM_CONDUIT_SIZE_TWO_INCH',
    'OTHER'
  ];

  isSectionChecked(section: string): boolean {
    return !!this.sectionToggleState[section];
  }

  private isSectionApplicable(section: string): boolean {
    switch (section) {
      case 'electricalWiringFormGroup':
        return !!this.costReviewElectricalWiring?.isElectricalWiringApplicable;
      case 'wiringInterlockFormGroup':
        return !!this.costReviewWiringInterlock?.isWiringInterlocksApplicable;
      case 'systemInterfacesFormGroup':
        return !!this.costReviewSystemInterfaces?.isSystemInterfacesApplicable;
      case 'dampersFormGroup':
        return !!this.costReviewDamperActuators?.isDampersApplicable;
      case 'smokeDampersFormGroup':
        return !!this.costReviewSmokeDampers?.isSmokeDampersApplicable;
      case 'meteringFormGroup':
        return !!this.costReviewMetering?.isMeteringApplicable;
      case 'integrationFormGroup':
        return !!this.costReviewIntegration?.isIntegrationApplicatble;
      case 'wiringResponsibilitiesFormGroup':
        return !!this.costReviewFireEnUsWiringResponsibilities?.isWiringResponsibilitiesApplicable;
      case 'managementStationFormGroup':
        return !!this.costReviewSecurityEnUsManagementStation?.isManagementStationChecked;
      case 'outsidePurchaseFormGroup':
        return !!this.costReviewEnUsOutsidePurchase?.isOutsidePurchasesApplicable;
      case 'executionScheduleFormGroup':
        return !this.optionalWorkflowCondition || !!this.costReviewFireEnUsExecutionSchedule?.isExecutionScheduleChecked;
      default:
        return true;
    }
  }

  private getSectionDisabledState(section: string): boolean {
    return this.isDisabled || !this.isSectionChecked(section);
  }

  private initializeSectionToggleState(): void {
    const sections = [
      'electricalWiringFormGroup',
      'wiringInterlockFormGroup',
      'systemInterfacesFormGroup',
      'dampersFormGroup',
      'smokeDampersFormGroup',
      'meteringFormGroup',
      'integrationFormGroup',
      'wiringResponsibilitiesFormGroup',
      'managementStationFormGroup',
      'outsidePurchaseFormGroup',
      'executionScheduleFormGroup'
    ];

    for (const section of sections) {
      this.sectionToggleState[section] = this.isSectionApplicable(section);
      this.sectionDisableState[section] = this.getSectionDisabledState(section);
    }
  }

  onSectionToggle(section: string, checked: boolean): void {
    this.sectionToggleState[section] = checked;
    this.sectionDisableState[section] = this.getSectionDisabledState(section);
    this.persistSectionToggleState(section, checked);
  }

  private persistSectionToggleState(section: string, checked: boolean): void {

    switch (section) {
      case 'electricalWiringFormGroup':
        this.costReviewElectricalWiring.isElectricalWiringApplicable = checked;
        break;
      case 'dampersFormGroup':
        this.costReviewDamperActuators.isDampersApplicable = checked;
        break;
      case 'executionScheduleFormGroup':
        this.costReviewFireEnUsExecutionSchedule.isExecutionScheduleChecked = checked;
        break;
      case 'smokeDampersFormGroup':
        this.costReviewSmokeDampers.isSmokeDampersApplicable = checked;
        break;
      case 'meteringFormGroup':
        this.costReviewMetering.isMeteringApplicable = checked;
        break;
      case 'integrationFormGroup':
        this.costReviewIntegration.isIntegrationApplicatble = checked;
        break;
      case 'wiringResponsibilitiesFormGroup':
        this.costReviewFireEnUsWiringResponsibilities.isWiringResponsibilitiesApplicable = checked;
        break;
      case 'managementStationFormGroup':
        this.costReviewSecurityEnUsManagementStation.isManagementStationChecked = checked;
        break;
      case 'systemInterfacesFormGroup' :
        this.costReviewSystemInterfaces.isSystemInterfacesApplicable = checked;
        break;
      case 'outsidePurchaseFormGroup':
        this.costReviewEnUsOutsidePurchase.isOutsidePurchasesApplicable = checked;
        break;
      case 'wiringInterlockFormGroup':
        this.costReviewWiringInterlock.isWiringInterlocksApplicable = checked;
        break;
    }
  }

  isSectionFieldsetDisabled(section: string): boolean {
    const disabled = this.getSectionDisabledState(section);
    this.sectionDisableState[section] = disabled;
    return disabled;
  }

  private _isAemOverride?: boolean;
  private _showSmokeDampersSectionOverride?: boolean;

  get isAem(): boolean {
    if (this._isAemOverride !== undefined) {
      return this._isAemOverride;
    }
    return (
      this.business === this.constants.BUSINESS_AUTOMATION ||
      this.business === this.constants.BUSINESS_MECHANICAL
    );
  }

  set isAem(value: boolean) {
    this._isAemOverride = value;
  }

  get showSmokeDampersSection(): boolean {
    if (this._showSmokeDampersSectionOverride !== undefined) {
      return this._showSmokeDampersSectionOverride;
    }
    return this.isAem && !!this.costReviewSmokeDampers;
  }

  set showSmokeDampersSection(value: boolean) {
    this._showSmokeDampersSectionOverride = value;
  }

  get isFireOrSecurity(): boolean {
    return this.business === this.constants.BUSINESS_FIRE || 
           this.business === this.constants.BUSINESS_SECURITY;
  }

  get isFire(): boolean {
    return this.business === this.constants.BUSINESS_FIRE;
  }

  get isSecurity(): boolean {
    return this.business === this.constants.BUSINESS_SECURITY;
  }

  get isAemWithHighSalePrice(): boolean {
    return this.isAem && this.isSalePriceGreaterThan150;
  }
  get isSubcontractorHasDPUNotSelected() : boolean{
    return this.isControlValueEqualToTranslation(this.electricalWiringFormGroup, 'contractorSubcontractorHasDPU', 'NO');
  }

  get isExecutionScheduleLocked(): boolean {
    return this.optionalWorkflowCondition && this.costReviewFireEnUsExecutionSchedule?.isExecutionScheduleChecked;
  }

  // Data objects actually used by forms
  costReviewDamperActuators = new CostReviewDampers();
  costReviewSmokeDampers = new CostReviewSmokeDampers();
  costReviewMetering = new CostReviewMetering();
  costReviewIntegration = new CostReviewIntegration();
  costReviewSystemInterfaces = new CostReviewFireEnUsSystemInterfaces();
  costReviewSecurityEnUsExecutionSchedule: CostReviewSecurityEnUsExecutionSchedule = new CostReviewSecurityEnUsExecutionSchedule();
  costReviewFireEnUsExecutionSchedule: CostReviewFireEnUsExecutionSchedule = new CostReviewFireEnUsExecutionSchedule();
  costReviewEnUsOutsidePurchase = new CostReviewFireEnUsOutsidePurchases();
  costReviewFireEnUsWiringResponsibilities = new CostReviewFireEnUsWiringResponsibilities();
  costReviewSecurityEnUsManagementStation = new CostReviewSecurityEnUsManagementStation();
  costReviewElectricalWiring: CostReviewElectricalWiring = new CostReviewElectricalWiring();
  costReviewWiringInterlock = new CostReviewWiringInterlocks();

  dampersFormGroup!: FormGroup<IDampersForm>;
  smokeDampersFormGroup!: FormGroup<ISmokeDampersForm>;
  meteringFormGroup!: FormGroup<IMeteringForm>;
  integrationFormGroup!: FormGroup<IIntegrationForm>;
  systemInterfacesFormGroup!: FormGroup<ISystemInterfacesForm>;
  executionScheduleFormGroup!: FormGroup<FireExecutionScheduleFormControls>;
  outsidePurchaseFormGroup!: FormGroup<IOutsidePurchaseForm>;
  wiringResponsibilitiesFormGroup!: FormGroup<IWiringResponsibilitiesForm>;
  managementStationFormGroup!: FormGroup<IManagementStationForm>;
  electricalWiringFormGroup!: FormGroup<IElectricalWiringForm>;
  wiringInterlockFormGroup!: FormGroup<IWiringInterlockForm>;
  estimateType = CostReviewEstimateType.None;

  constructor(
    private readonly reviewService: ReviewService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly commonDataService: CommonDataService,
    private readonly reviewDetailsTabService: ReviewDetailTabsService
  ) { }

  ngOnInit() {
     this.estimateType = this.convertToEstimateType(
      this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType().optionalEstimateType
    );
    this.getReviewLibraryState();
    this.isReadMode = this.commonDataService.libraryReadOnlyMode;
    this.isSalePriceGreaterThan150 = this.estimateType === CostReviewEstimateType.EstimateGreater150K;
    this.managementStationList = this.getDropdownValues(this.constants.MANAGEMENT_STATION);
    this.optionalWorkflowCondition = this.reviewDetailsTabService.getOptionalWorkflowAndEstimateType()?.optionalWorkflow || false;
    this.safeInitializeAndMap();
    this.createAllFormGroups();
    this.initializeSectionToggleState();
    this.refreshSystemInterfacesDisabledState();
    this.setupReadOnlyMode();
    this.subscribeToReviewFormDataSyncRequest();
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }

  private convertToEstimateType(optionalEstimateType: CostReviewEstimateType | undefined): CostReviewEstimateType {
    switch (optionalEstimateType) {
      case CostReviewEstimateType.EstimateLess50K:
        return CostReviewEstimateType.EstimateLess50K;
      case CostReviewEstimateType.Estimate50_150K:
        return CostReviewEstimateType.Estimate50_150K;      
      case CostReviewEstimateType.EstimateGreater150K:
        return CostReviewEstimateType.EstimateGreater150K;
      default:
        return CostReviewEstimateType.None;
    }
  }

  ngOnDestroy(): void {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private pushFormDataToService(): void {
    switch (this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL:
        this.pushDampersFormData();
        this.pushSmokeDampersFormData();
        this.pushMeteringFormData();
        this.pushIntegrationFormData();
        this.pushElectricalWiringFormData();
        this.pushWiringInterlockFormData();
        break;

      case this.constants.BUSINESS_FIRE:
        this.pushSystemInterfacesFormData();
        this.pushOutsidePurchaseFormData();
        this.pushWiringResponsibilitiesFormData();
        this.pushElectricalWiringFormData();
        this.pushExecutionScheduleFireFormData();
        break;

      case this.constants.BUSINESS_SECURITY:
        this.pushElectricalWiringFormData();
        this.pushWiringResponsibilitiesFormData();
        this.pushManagementStationFormData(); 
        break;
    }

    this.attachReviewObjectReferences();
  }

  private attachReviewObjectReferences(): void {
    const reviewObject = this.reviewService.getOrCreateReviewObjectForBusiness(this.business);
    if (!reviewObject) {
      return;
    }

    switch (this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL: {
        const attrs = reviewObject as CostReviewAttributes;
        attrs.costReviewDampers = this.costReviewDamperActuators;
        attrs.costReviewSmokeDampers = this.costReviewSmokeDampers;
        attrs.costReviewMetering = this.costReviewMetering;
        attrs.costReviewIntegration = this.costReviewIntegration;
        attrs.costReviewElectricalWiring = this.costReviewElectricalWiring;
        attrs.costReviewWiringInterlocks = this.costReviewWiringInterlock;
        break;
      }

      case this.constants.BUSINESS_FIRE: {
        const fire = reviewObject as CostReviewFireEnUsAttributes;
        fire.costReviewFireEnUsSystemInterfaces = this.costReviewSystemInterfaces;
        fire.costReviewFireEnUsExecutionSchedule = this.costReviewFireEnUsExecutionSchedule;
        fire.costReviewFireEnUsOutsidePurchases = this.costReviewEnUsOutsidePurchase;
        fire.costReviewFireEnUsWiringResponsibilities = this.costReviewFireEnUsWiringResponsibilities;
        fire.costReviewElectricalWiring = this.costReviewElectricalWiring;
        break;
      }

      case this.constants.BUSINESS_SECURITY: {
        const sec = reviewObject as CostReviewSecurityEnUsAttributes;
        sec.costReviewSecurityEnUsExecutionSchedule = this.costReviewSecurityEnUsExecutionSchedule;
        sec.costReviewSecurityEnUsManagementStation = this.costReviewSecurityEnUsManagementStation;
        sec.costReviewFireEnUsWiringResponsibilities = this.costReviewFireEnUsWiringResponsibilities;
        sec.costReviewElectricalWiring = this.costReviewElectricalWiring;
        break;
      }
    }
  }

  private pushDampersFormData(): void {
    if (!this.dampersFormGroup) {
      return;
    }
    if (!this.costReviewDamperActuators) {
      this.costReviewDamperActuators = new CostReviewDampers();
    }

    const formData: DampersFormValue = this.dampersFormGroup.getRawValue();
    this.costReviewDamperActuators.receivedBindingQuote = this.getParameterCode(this.constants.YES_NO, formData.receivedBindingQuote, this.costReviewDamperActuators.receivedBindingQuote);
    this.costReviewDamperActuators.damperQuoteAmount = this.commonFunctionsService.toNumberOrCurrent(formData.damperQuoteAmount, this.costReviewDamperActuators.damperQuoteAmount);
    this.costReviewDamperActuators.numberOfDampers = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfDampers, this.costReviewDamperActuators.numberOfDampers);
    this.costReviewDamperActuators.manufacturer = formData.manufacturer;
    this.costReviewDamperActuators.actuatorsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.actuatorsFurnishedBy, this.costReviewDamperActuators.actuatorsFurnishedBy);
    this.costReviewDamperActuators.actuatorsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.actuatorsInstalledBy, this.costReviewDamperActuators.actuatorsInstalledBy);
    this.costReviewDamperActuators.actuatorsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.actuatorsWiredBy, this.costReviewDamperActuators.actuatorsWiredBy);
    this.costReviewDamperActuators.specialApplications = formData.specialApplications;
  }

  private pushSmokeDampersFormData(): void {
    if (!this.smokeDampersFormGroup) {
      return;
    }
    if (!this.costReviewSmokeDampers) {
      this.costReviewSmokeDampers = new CostReviewSmokeDampers();
    }

    const formData: SmokeDampersFormValue = this.smokeDampersFormGroup.getRawValue();
    this.costReviewSmokeDampers.receivedBindingQuote = this.getParameterCode(this.constants.YES_NO, formData.receivedBindingQuote, this.costReviewSmokeDampers.receivedBindingQuote);
    this.costReviewSmokeDampers.smokeDamperQuoteAmount = this.commonFunctionsService.toNumberOrCurrent(formData.smokeDamperQuoteAmount, this.costReviewSmokeDampers.smokeDamperQuoteAmount);
    this.costReviewSmokeDampers.numberOfDampers = this.commonFunctionsService.toNumberOrCurrent(formData.numberOfDampers, this.costReviewSmokeDampers.numberOfDampers);
    this.costReviewSmokeDampers.manufacturer = formData.manufacturer;
    this.costReviewSmokeDampers.actuatorsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.actuatorsFurnishedBy, this.costReviewSmokeDampers.actuatorsFurnishedBy);
    this.costReviewSmokeDampers.actuatorsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.actuatorsWiredBy, this.costReviewSmokeDampers.actuatorsWiredBy);
    this.costReviewSmokeDampers.smokeDamperEndSwitchMonitoring = this.getParameterCode(this.constants.YES_NO, formData.smokeDamperEndSwitchMonitoring, this.costReviewSmokeDampers.smokeDamperEndSwitchMonitoring);
    this.costReviewSmokeDampers.ifYesWiring = this.getParameterCode(this.constants.HARDWIRED_SOFTWARE, formData.ifYesWiring, this.costReviewSmokeDampers.ifYesWiring);
  }

  private pushMeteringFormData(): void {
    if (!this.meteringFormGroup) {
      return;
    }
    if (!this.costReviewMetering) {
      this.costReviewMetering = new CostReviewMetering();
    }

    const formData: MeteringFormValue = this.meteringFormGroup.getRawValue();
    this.costReviewMetering.manufacturer = formData.manufacturer;
    this.costReviewMetering.installation = this.getParameterCode(this.constants.HARDWIRED_INTEGRATION, formData.installation, this.costReviewMetering.installation);
    this.costReviewMetering.unitsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsFurnishedBy, this.costReviewMetering.unitsFurnishedBy);
    this.costReviewMetering.unitsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsInstalledBy, this.costReviewMetering.unitsInstalledBy);
    this.costReviewMetering.unitsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsWiredBy, this.costReviewMetering.unitsWiredBy);
  }

  private pushIntegrationFormData(): void {
    if (!this.integrationFormGroup) {
      return;
    }
    if (!this.costReviewIntegration) {
      this.costReviewIntegration = new CostReviewIntegration();
    }

    const formData: IntegrationFormValue = this.integrationFormGroup.getRawValue();
    this.costReviewIntegration.systemsThatRequireIntegration = formData.systemsThatRequireIntegration;
    this.costReviewIntegration.integrationProtocol = formData.integrationProtocol;
    this.costReviewIntegration.unitsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsFurnishedBy, this.costReviewIntegration.unitsFurnishedBy);
    this.costReviewIntegration.unitsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsInstalledBy, this.costReviewIntegration.unitsInstalledBy);
    this.costReviewIntegration.unitsWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.unitsWiredBy, this.costReviewIntegration.unitsWiredBy);
    this.costReviewIntegration.graphicsRequired = this.getParameterCode(this.constants.YES_NO, formData.graphicsRequired, this.costReviewIntegration.graphicsRequired);
  }

  private pushSystemInterfacesFormData(): void {
    if (!this.systemInterfacesFormGroup) {
      return;
    }
    if (!this.costReviewSystemInterfaces) {
      this.costReviewSystemInterfaces = new CostReviewFireEnUsSystemInterfaces();
    }

    const formData: SystemInterfacesFormValue = this.systemInterfacesFormGroup.getRawValue();
    this.costReviewSystemInterfaces.systemInterfaces = this.toNumericArray(formData.systemInterfaces as Array<string | number> | undefined);
    this.costReviewSystemInterfaces.fireSmokeDampers = this.toNumericArray(formData.fireSmokeDampers as Array<string | number> | undefined);
    this.costReviewSystemInterfaces.ledAnnunciator = this.toNumericArray(formData.ledAnnunciator as Array<string | number> | undefined);
    this.costReviewSystemInterfaces.alphaNumericAnnunciator = this.toNumericArray(formData.alphaNumericAnnunciator as Array<string | number> | undefined);
    this.costReviewSystemInterfaces.otherNotes = formData.otherNotes;
  }

  private pushExecutionScheduleFireFormData(): void {
    if (!this.executionScheduleFormGroup) {
      return;
    }
    if (!this.costReviewFireEnUsExecutionSchedule) {
      this.costReviewFireEnUsExecutionSchedule = new CostReviewFireEnUsExecutionSchedule();
    }

    const formData: FireExecutionScheduleFormValue = this.executionScheduleFormGroup.getRawValue();
    this.costReviewFireEnUsExecutionSchedule.phasedInstallationOccupancy = this.getParameterCode(this.constants.YES_NO, formData.phasedInstallationOccupancy, this.costReviewFireEnUsExecutionSchedule.phasedInstallationOccupancy);
    this.costReviewFireEnUsExecutionSchedule.projectSchedulePublished = this.getParameterCode(this.constants.YES_NO, formData.projectSchedulePublished, this.costReviewFireEnUsExecutionSchedule.projectSchedulePublished);
    this.costReviewFireEnUsExecutionSchedule.firePlanIdentified = this.getParameterCode(this.constants.YES_NO, formData.firePlanIdentified, this.costReviewFireEnUsExecutionSchedule.firePlanIdentified);
    this.costReviewFireEnUsExecutionSchedule.localOfficeTechnicalCompetencies = this.getParameterCode(this.constants.YES_NO, formData.localOfficeTechnicalCompetencies, this.costReviewFireEnUsExecutionSchedule.localOfficeTechnicalCompetencies);
    this.costReviewFireEnUsExecutionSchedule.localOfficeStaffedForWork = this.getParameterCode(this.constants.YES_NO, formData.localOfficeStaffedForWork, this.costReviewFireEnUsExecutionSchedule.localOfficeStaffedForWork);
    this.costReviewFireEnUsExecutionSchedule.specialCommissioningProceduresRequired = this.getParameterCode(this.constants.YES_NO, formData.specialCommissioningProceduresRequired, this.costReviewFireEnUsExecutionSchedule.specialCommissioningProceduresRequired);
    this.costReviewFireEnUsExecutionSchedule.designConsiderations = this.getParameterCode(this.constants.YES_NO, formData.designConsiderations, this.costReviewFireEnUsExecutionSchedule.designConsiderations);
    this.costReviewFireEnUsExecutionSchedule.unionProject = this.getParameterCode(this.constants.YES_NO, formData.unionProject, this.costReviewFireEnUsExecutionSchedule.unionProject);
    this.costReviewFireEnUsExecutionSchedule.specialTaggingRequirements = this.getParameterCode(this.constants.YES_NO, formData.specialTaggingRequirements, this.costReviewFireEnUsExecutionSchedule.specialTaggingRequirements);
    this.costReviewFireEnUsExecutionSchedule.responsibleForHazardousMaterials = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.responsibleForHazardousMaterials, this.costReviewFireEnUsExecutionSchedule.responsibleForHazardousMaterials);
    this.costReviewFireEnUsExecutionSchedule.confinedWorkSpace = this.getParameterCode(this.constants.YES_NO, formData.confinedWorkSpace, this.costReviewFireEnUsExecutionSchedule.confinedWorkSpace);
    this.costReviewFireEnUsExecutionSchedule.responsibleForInaccessibleAccess = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.responsibleForInaccessibleAccess, this.costReviewFireEnUsExecutionSchedule.responsibleForInaccessibleAccess);
  }

  private pushOutsidePurchaseFormData(): void {
    if (!this.outsidePurchaseFormGroup) {
      return;
    }
    if (!this.costReviewEnUsOutsidePurchase) {
      this.costReviewEnUsOutsidePurchase = new CostReviewFireEnUsOutsidePurchases();
    }

    const formData: OutsidePurchaseFormValue = this.outsidePurchaseFormGroup.getRawValue();
    this.costReviewEnUsOutsidePurchase.specialFabricationItems = this.getParameterCode(this.constants.YES_NO, formData.specialFabricationItems, this.costReviewEnUsOutsidePurchase.specialFabricationItems);
    this.costReviewEnUsOutsidePurchase.heatersRequired = this.getParameterCode(this.constants.YES_NO, formData.heatersRequired, this.costReviewEnUsOutsidePurchase.heatersRequired);
    this.costReviewEnUsOutsidePurchase.thirdPartyMaterialsIdentified = this.getParameterCode(this.constants.YES_NO, formData.thirdPartyMaterialsIdentified, this.costReviewEnUsOutsidePurchase.thirdPartyMaterialsIdentified);
    this.costReviewEnUsOutsidePurchase.supplierPartNumbers = this.getParameterCode(this.constants.YES_NO, formData.supplierPartNumbers, this.costReviewEnUsOutsidePurchase.supplierPartNumbers);
    this.costReviewEnUsOutsidePurchase.outsidePurchaseSystems = this.getParameterCode(this.constants.YES_NO, formData.outsidePurchaseSystems, this.costReviewEnUsOutsidePurchase.outsidePurchaseSystems);
    this.costReviewEnUsOutsidePurchase.ifYesRequireManufacturer = this.getParameterCode(this.constants.YES_NO, formData.ifYesRequireManufacturer, this.costReviewEnUsOutsidePurchase.ifYesRequireManufacturer);
  }

  private pushWiringResponsibilitiesFormData(): void {
    if (!this.wiringResponsibilitiesFormGroup) {
      return;
    }
    if (!this.costReviewFireEnUsWiringResponsibilities) {
      this.costReviewFireEnUsWiringResponsibilities = new CostReviewFireEnUsWiringResponsibilities();
    }

    const formData: WiringResponsibilitiesFormValue = this.wiringResponsibilitiesFormGroup.getRawValue();
    this.costReviewFireEnUsWiringResponsibilities.fieldTerminations = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.fieldTerminations, this.costReviewFireEnUsWiringResponsibilities.fieldTerminations);
    this.costReviewFireEnUsWiringResponsibilities.panelTerminations = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.panelTerminations, this.costReviewFireEnUsWiringResponsibilities.panelTerminations);
    this.costReviewFireEnUsWiringResponsibilities.padAndRemoteAnnunciator = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.padAndRemoteAnnunciator, this.costReviewFireEnUsWiringResponsibilities.padAndRemoteAnnunciator);
    this.costReviewFireEnUsWiringResponsibilities.highVoltageWiring = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.highVoltageWiring, this.costReviewFireEnUsWiringResponsibilities.highVoltageWiring);
    this.costReviewFireEnUsWiringResponsibilities.supply24Vdc = this.getParameterCode(this.constants.YES_NO, formData.supply24Vdc, this.costReviewFireEnUsWiringResponsibilities.supply24Vdc);
    this.costReviewFireEnUsWiringResponsibilities.thirdPartyTerminations = this.getParameterCode(this.constants.YES_NO, formData.thirdPartyTerminations, this.costReviewFireEnUsWiringResponsibilities.thirdPartyTerminations);
    this.costReviewFireEnUsWiringResponsibilities.fiberOpticsRequired = this.getParameterCode(this.constants.YES_NO, formData.fiberOpticsRequired, this.costReviewFireEnUsWiringResponsibilities.fiberOpticsRequired);
    this.costReviewFireEnUsWiringResponsibilities.fiberFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.fiberFurnishedBy, this.costReviewFireEnUsWiringResponsibilities.fiberFurnishedBy);
    this.costReviewFireEnUsWiringResponsibilities.fiberInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.fiberInstalledBy, this.costReviewFireEnUsWiringResponsibilities.fiberInstalledBy);
    this.costReviewFireEnUsWiringResponsibilities.fiberTerminationsBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.fiberTerminationsBy, this.costReviewFireEnUsWiringResponsibilities.fiberTerminationsBy);
    this.costReviewFireEnUsWiringResponsibilities.ductHousingsAndSampling = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.ductHousingsAndSampling, this.costReviewFireEnUsWiringResponsibilities.ductHousingsAndSampling);
    this.costReviewFireEnUsWiringResponsibilities.providesPowerForEnclosureHeaters = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.providesPowerForEnclosureHeaters, this.costReviewFireEnUsWiringResponsibilities.providesPowerForEnclosureHeaters);
    this.costReviewFireEnUsWiringResponsibilities.fireSmokeDamperValve = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.fireSmokeDamperValve, this.costReviewFireEnUsWiringResponsibilities.fireSmokeDamperValve);
    this.costReviewFireEnUsWiringResponsibilities.powerForActuatorsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.powerForActuatorsFurnishedBy, this.costReviewFireEnUsWiringResponsibilities.powerForActuatorsFurnishedBy);
    this.costReviewFireEnUsWiringResponsibilities.powerWiring = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.powerWiring, this.costReviewFireEnUsWiringResponsibilities.powerWiring);
    this.costReviewFireEnUsWiringResponsibilities.wireCablingFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.wireCablingFurnishedBy, this.costReviewFireEnUsWiringResponsibilities.wireCablingFurnishedBy);
    this.costReviewFireEnUsWiringResponsibilities.freeRunCableAcceptable = this.getParameterCode(this.constants.YES_NO, formData.freeRunCableAcceptable, this.costReviewFireEnUsWiringResponsibilities.freeRunCableAcceptable);
    this.costReviewFireEnUsWiringResponsibilities.lowVoltageWiring = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.lowVoltageWiring, this.costReviewFireEnUsWiringResponsibilities.lowVoltageWiring);
    this.costReviewFireEnUsWiringResponsibilities.devicesInstalled = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.devicesInstalled, this.costReviewFireEnUsWiringResponsibilities.devicesInstalled);
    this.costReviewFireEnUsWiringResponsibilities.devicesWired = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.devicesWired, this.costReviewFireEnUsWiringResponsibilities.devicesWired);
    this.costReviewFireEnUsWiringResponsibilities.equipmentInstalled = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.equipmentInstalled, this.costReviewFireEnUsWiringResponsibilities.equipmentInstalled);
  }

  private pushManagementStationFormData(): void {
    if (!this.managementStationFormGroup) {
      return;
    }
    if (!this.costReviewSecurityEnUsManagementStation) {
      this.costReviewSecurityEnUsManagementStation = new CostReviewSecurityEnUsManagementStation();
    }

    const formData: ManagementStationFormValue = this.managementStationFormGroup.getRawValue();
    this.costReviewSecurityEnUsManagementStation.managementStation = formData.managementStation;
  }

  private pushElectricalWiringFormData(): void {
    if (!this.electricalWiringFormGroup) {
      return;
    }
    if (!this.costReviewElectricalWiring) {
      this.costReviewElectricalWiring = new CostReviewElectricalWiring();
    }

    const formData: ElectricalWiringFormValue = this.electricalWiringFormGroup.getRawValue();
    this.costReviewElectricalWiring.electricalSubcontractor = formData.electricalSubcontractor;
    this.costReviewElectricalWiring.amountBudgeted = this.commonFunctionsService.toNumberOrCurrent(formData.amountBudgeted, this.costReviewElectricalWiring.amountBudgeted);
    this.costReviewElectricalWiring.threeBidsSecured = this.getParameterCode(this.constants.YES_NO, formData.threeBidsSecured, this.costReviewElectricalWiring.threeBidsSecured);
    this.costReviewElectricalWiring.vendorNameBidOne = formData.vendorNameBidOne;
    this.costReviewElectricalWiring.vendorNameBidTwo = formData.vendorNameBidTwo;
    this.costReviewElectricalWiring.vendorNameBidThree = formData.vendorNameBidThree;
    this.costReviewElectricalWiring.requestedQuotesFrom = formData.requestedQuotesFrom;
    this.costReviewElectricalWiring.fpmInvolved = this.getParameterCode(this.constants.YES_NO, formData.fpmInvolved, this.costReviewElectricalWiring.fpmInvolved);
    this.costReviewElectricalWiring.quoteVariance = this.commonFunctionsService.toNumberOrCurrent(formData.quoteVariance, this.costReviewElectricalWiring.quoteVariance);
    this.costReviewElectricalWiring.receivedBindingQuote = this.getParameterCode(this.constants.YES_NO, formData.receivedBindingQuote, this.costReviewElectricalWiring.receivedBindingQuote);
    this.costReviewElectricalWiring.subIsBauCertified = this.getParameterCode(this.constants.YES_NO, formData.subIsBauCertified, this.costReviewElectricalWiring.subIsBauCertified);
    this.costReviewElectricalWiring.securityPMInvolved = this.getParameterCode(this.constants.YES_NO, formData.securityPMInvolved, this.costReviewElectricalWiring.securityPMInvolved);
    this.costReviewElectricalWiring.subHasProjectSpecifications = this.getParameterCode(this.constants.YES_NO, formData.subHasProjectSpecifications, this.costReviewElectricalWiring.subHasProjectSpecifications);
    this.costReviewElectricalWiring.subHasCapacityToCompleteWork = this.getParameterCode(this.constants.YES_NO, formData.subHasCapacityToCompleteWork, this.costReviewElectricalWiring.subHasCapacityToCompleteWork);
    this.costReviewElectricalWiring.subCarriesBond = this.getParameterCode(this.constants.YES_NO, formData.subCarriesBond, this.costReviewElectricalWiring.subCarriesBond);
    this.costReviewElectricalWiring.subCarriesBondReason = formData.subCarriesBondReason;
    this.costReviewElectricalWiring.subsNotifiedSecurity = this.getParameterCode(this.constants.YES_NO, formData.subsNotifiedSecurity, this.costReviewElectricalWiring.subsNotifiedSecurity);
    this.costReviewElectricalWiring.contractValue = this.commonFunctionsService.toNumberOrCurrent(formData.contractValue, this.costReviewElectricalWiring.contractValue);
    this.costReviewElectricalWiring.allConduitRequired = this.getParameterCode(this.constants.YES_NO, formData.allConduitRequired, this.costReviewElectricalWiring.allConduitRequired);
    this.costReviewElectricalWiring.conduitType = this.getParameterCode(this.constants.CONDUIT_TYPE, formData.conduitType, this.costReviewElectricalWiring.conduitType);
    this.costReviewElectricalWiring.fiberOpticsSecurity = this.getParameterCode(this.constants.YES_NO, formData.fiberOpticsSecurity, this.costReviewElectricalWiring.fiberOpticsSecurity);
    this.costReviewElectricalWiring.wiringType = this.getParameterCode(this.constants.ELECTRICALWIRING_WIRING_TYPE, formData.wiringType, this.costReviewElectricalWiring.wiringType);
    this.costReviewElectricalWiring.minimumConduitSize = this.getIndexBasedParameterCode(this.minimumConduitSizeOptions, formData.minimumConduitSize, this.costReviewElectricalWiring.minimumConduitSize);
    this.costReviewElectricalWiring.safetiesFurnishedBy = formData.safetiesFurnishedBy;
    this.costReviewElectricalWiring.contractorSubcontractorHasDPU = this.getParameterCode(this.constants.YES_NO, formData.contractorSubcontractorHasDPU, this.costReviewElectricalWiring.contractorSubcontractorHasDPU);
    this.costReviewElectricalWiring.ifNoWillOneBeProvided = this.getParameterCode(this.constants.YES_NO, formData.ifNoWillOneBeProvided, this.costReviewElectricalWiring.ifNoWillOneBeProvided);
  }

  private pushWiringInterlockFormData(): void {
    if (!this.wiringInterlockFormGroup) {
      return;
    }
    if (!this.costReviewWiringInterlock) {
      this.costReviewWiringInterlock = new CostReviewWiringInterlocks();
    }

    const formData: WiringInterlockFormValue = this.wiringInterlockFormGroup.getRawValue();
    this.costReviewWiringInterlock.boilerChillerWiringDetailed = this.getParameterCode(this.constants.YES_NO, formData.boilerChillerWiringDetailed, this.costReviewWiringInterlock.boilerChillerWiringDetailed);
    this.costReviewWiringInterlock.flowSwitchesWiredBy = formData.flowSwitchesWiredBy;
    this.costReviewWiringInterlock.chemicalFeedSystemWiringRequired = this.getParameterCode(this.constants.YES_NO, formData.chemicalFeedSystemWiringRequired, this.costReviewWiringInterlock.chemicalFeedSystemWiringRequired);
    this.costReviewWiringInterlock.sumpLevelControllerWiringRequired = this.getParameterCode(this.constants.YES_NO, formData.sumpLevelControllerWiringRequired, this.costReviewWiringInterlock.sumpLevelControllerWiringRequired);
    this.costReviewWiringInterlock.other = formData.other;
    this.costReviewWiringInterlock.splitSystemDxWiringRequired = this.getParameterCode(this.constants.YES_NO, formData.splitSystemDxWiringRequired, this.costReviewWiringInterlock.splitSystemDxWiringRequired);
    this.costReviewWiringInterlock.exhaustFanWiring = this.getParameterCode(this.constants.YES_NO, formData.exhaustFanWiring, this.costReviewWiringInterlock.exhaustFanWiring);
    this.costReviewWiringInterlock.exhaustFanDamperInterlocks = this.getParameterCode(this.constants.YES_NO, formData.exhaustFanDamperInterlocks, this.costReviewWiringInterlock.exhaustFanDamperInterlocks);
    this.costReviewWiringInterlock.coolingTowerWiring = this.commonFunctionsService.toNumberArray(formData.coolingTowerWiring, this.coolingTowerWiringOptions);
    this.costReviewWiringInterlock.panelPowerWiredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.panelPowerWiredBy, this.costReviewWiringInterlock.panelPowerWiredBy);
    this.costReviewWiringInterlock.smokeDetectorsInstalledBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.smokeDetectorsInstalledBy, this.costReviewWiringInterlock.smokeDetectorsInstalledBy);
    this.costReviewWiringInterlock.smokeDetectorsFurnishedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.smokeDetectorsFurnishedBy, this.costReviewWiringInterlock.smokeDetectorsFurnishedBy);
    this.costReviewWiringInterlock.interlocks120Volt = this.getParameterCode(this.constants.YES_NO, formData.interlocks120Volt, this.costReviewWiringInterlock.interlocks120Volt);
    this.costReviewWiringInterlock.smokeDetectorsLocatedBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.smokeDetectorsLocatedBy, this.costReviewWiringInterlock.smokeDetectorsLocatedBy);
    this.costReviewWiringInterlock.siemensPanelsPoweredBy = this.getParameterCode(this.constants.SIEMENS_OTHER, formData.siemensPanelsPoweredBy, this.costReviewWiringInterlock.siemensPanelsPoweredBy);
  }

  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadMode = false;
          this.enableAllFormGroups();
        } else {
          this.isReadMode = readOnly;
          this.disableAllFormGroups();
        }
      });
  }

  private disableAllFormGroups(): void {
    this.dampersFormGroup?.disable();
    this.smokeDampersFormGroup?.disable();
    this.meteringFormGroup?.disable();
    this.integrationFormGroup?.disable();
    this.systemInterfacesFormGroup?.disable();
    this.executionScheduleFormGroup?.disable();
    this.outsidePurchaseFormGroup?.disable();
    this.wiringResponsibilitiesFormGroup?.disable();
    this.managementStationFormGroup?.disable();
    this.electricalWiringFormGroup?.disable();
    this.wiringInterlockFormGroup?.disable();
  }

  private enableAllFormGroups(): void {
    this.dampersFormGroup?.enable();
    this.smokeDampersFormGroup?.enable();
    this.meteringFormGroup?.enable();
    this.integrationFormGroup?.enable();
    this.systemInterfacesFormGroup?.enable();
    this.executionScheduleFormGroup?.enable();
    this.outsidePurchaseFormGroup?.enable();
    this.wiringResponsibilitiesFormGroup?.enable();
    this.managementStationFormGroup?.enable();
    this.electricalWiringFormGroup?.enable();
    this.wiringInterlockFormGroup?.enable();
  }

  safeInitializeAndMap() {
    const costReviewData = this.reviewService.costReviewDetails.reviewObject;
    this.business = this.reviewService.getBusiness();

    if (!costReviewData) {
      return;
    }

    switch (this.business) {
      case this.constants.BUSINESS_AUTOMATION:
      case this.constants.BUSINESS_ELECTRICAL:
      case this.constants.BUSINESS_MECHANICAL: {
        const attrs = costReviewData as CostReviewAttributes;
        this.costReviewDamperActuators = attrs.costReviewDampers ?? this.costReviewDamperActuators;
        this.costReviewSmokeDampers = attrs.costReviewSmokeDampers ?? this.costReviewSmokeDampers;
        this.costReviewMetering = attrs.costReviewMetering ?? this.costReviewMetering;
        this.costReviewIntegration = attrs.costReviewIntegration ?? this.costReviewIntegration;
        this.costReviewElectricalWiring = attrs.costReviewElectricalWiring ?? this.costReviewElectricalWiring;
        this.costReviewWiringInterlock = attrs.costReviewWiringInterlocks ?? this.costReviewWiringInterlock;
        break;
      }
      case this.constants.BUSINESS_FIRE: {
        const fire = costReviewData as CostReviewFireEnUsAttributes;
        this.costReviewSystemInterfaces = fire.costReviewFireEnUsSystemInterfaces ?? this.costReviewSystemInterfaces;
        this.costReviewFireEnUsExecutionSchedule = fire.costReviewFireEnUsExecutionSchedule ?? this.costReviewFireEnUsExecutionSchedule;
        this.costReviewEnUsOutsidePurchase = fire.costReviewFireEnUsOutsidePurchases ?? this.costReviewEnUsOutsidePurchase;
        this.costReviewFireEnUsWiringResponsibilities = fire.costReviewFireEnUsWiringResponsibilities ?? this.costReviewFireEnUsWiringResponsibilities;
        this.costReviewElectricalWiring = fire.costReviewElectricalWiring ?? this.costReviewElectricalWiring;
        break;
      }
      case this.constants.BUSINESS_SECURITY: {
        const sec = costReviewData as CostReviewSecurityEnUsAttributes;
        this.costReviewSecurityEnUsExecutionSchedule = sec.costReviewSecurityEnUsExecutionSchedule ?? this.costReviewSecurityEnUsExecutionSchedule;
        this.costReviewSecurityEnUsManagementStation = sec.costReviewSecurityEnUsManagementStation ?? this.costReviewSecurityEnUsManagementStation;
        // Security uses fire wiring responsibilities model per existing data set
        this.costReviewFireEnUsWiringResponsibilities = sec.costReviewFireEnUsWiringResponsibilities ?? this.costReviewFireEnUsWiringResponsibilities;
        this.costReviewElectricalWiring = sec.costReviewElectricalWiring ?? this.costReviewElectricalWiring;
        break;
      }
    }
  }

  createAllFormGroups() {
    if(this.business === this.constants.BUSINESS_AUTOMATION || this.business === this.constants.BUSINESS_MECHANICAL) {
      this.createDampersForm();
      this.createSmokeDampersForm();
      this.createMeteringForm();
      this.createIntegrationForm();
      this.createElectricalWiringForm();
      this.createWiringInterlockForm();
    }
    
    if (this.business === this.constants.BUSINESS_FIRE) {
      this.createSystemInterfacesForm();
      this.createOutsidePurchasesForm();
      this.createWiringResponsibilitiesForm(); // fire wiring responsibilities
      this.createElectricalWiringForm();
      this.createExecutionScheduleForm();
    }

    if (this.business === this.constants.BUSINESS_SECURITY) {
      this.createElectricalWiringForm();
      this.createWiringResponsibilitiesForm(); // security wiring responsibilities (same model)
      this.createManagementStationForm();
    }
  }

  createDampersForm() {
    this.dampersFormGroup = this.formBuilder.group({
      receivedBindingQuote: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDamperActuators.receivedBindingQuote)),
      damperQuoteAmount: this.formBuilder.control(this.costReviewDamperActuators.damperQuoteAmount || 0),
      numberOfDampers: this.formBuilder.control(this.costReviewDamperActuators.numberOfDampers || 0),
      manufacturer: this.formBuilder.control(this.costReviewDamperActuators.manufacturer),
      actuatorsFurnishedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewDamperActuators.actuatorsFurnishedBy)),
      actuatorsInstalledBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewDamperActuators.actuatorsInstalledBy)),
      actuatorsWiredBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewDamperActuators.actuatorsWiredBy)),
      specialApplications: this.formBuilder.control(this.costReviewDamperActuators.specialApplications)
    });
  }

  createSmokeDampersForm() {
    this.smokeDampersFormGroup = this.formBuilder.group({
      receivedBindingQuote: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSmokeDampers.receivedBindingQuote)),
      smokeDamperQuoteAmount: this.formBuilder.control(this.costReviewSmokeDampers.smokeDamperQuoteAmount || 0),
      numberOfDampers: this.formBuilder.control(this.costReviewSmokeDampers.numberOfDampers || 0),
      manufacturer: this.formBuilder.control(this.costReviewSmokeDampers.manufacturer),
      actuatorsFurnishedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewSmokeDampers.actuatorsFurnishedBy)),
      actuatorsWiredBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewSmokeDampers.actuatorsWiredBy)),
      smokeDamperEndSwitchMonitoring: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSmokeDampers.smokeDamperEndSwitchMonitoring?.toString())),
      ifYesWiring: this.formBuilder.control(this.getParameterValues(this.constants.HARDWIRED_SOFTWARE, this.costReviewSmokeDampers.ifYesWiring?.toString())),
    });
  }

  createMeteringForm() {
    this.meteringFormGroup = this.formBuilder.group({
      manufacturer: this.formBuilder.control(this.costReviewMetering.manufacturer),
      installation: this.formBuilder.control(this.getParameterValues(this.constants.HARDWIRED_INTEGRATION, this.costReviewMetering.installation)),
      unitsFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewMetering.unitsFurnishedBy)),
      unitsInstalledBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewMetering.unitsInstalledBy)),
      unitsWiredBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewMetering.unitsWiredBy))
    });
  }

  createIntegrationForm() {
    this.integrationFormGroup = this.formBuilder.group({
      systemsThatRequireIntegration: this.formBuilder.control(this.costReviewIntegration.systemsThatRequireIntegration),
      integrationProtocol: this.formBuilder.control(this.costReviewIntegration.integrationProtocol || []),
      unitsFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewIntegration.unitsFurnishedBy)),
      unitsInstalledBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewIntegration.unitsInstalledBy)),
      unitsWiredBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewIntegration.unitsWiredBy)),
      graphicsRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewIntegration.graphicsRequired))
    });
  }

  createSystemInterfacesForm() {
    this.systemInterfacesFormGroup = this.formBuilder.group({
      systemInterfaces: this.formBuilder.control(this.costReviewSystemInterfaces.systemInterfaces || []),
      fireSmokeDampers: this.formBuilder.control(this.costReviewSystemInterfaces.fireSmokeDampers || []),
      ledAnnunciator: this.formBuilder.control(this.costReviewSystemInterfaces.ledAnnunciator || []),
      alphaNumericAnnunciator: this.formBuilder.control(this.costReviewSystemInterfaces.alphaNumericAnnunciator || []),
      otherNotes: this.formBuilder.control(this.costReviewSystemInterfaces.otherNotes)
    });
  }

  createExecutionScheduleForm() {
    this.executionScheduleFormGroup = this.formBuilder.group({
      phasedInstallationOccupancy: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.phasedInstallationOccupancy)),
      projectSchedulePublished: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.projectSchedulePublished)),
      firePlanIdentified: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.firePlanIdentified)),
      localOfficeTechnicalCompetencies: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.localOfficeTechnicalCompetencies)),
      localOfficeStaffedForWork: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.localOfficeStaffedForWork)),
      specialCommissioningProceduresRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.specialCommissioningProceduresRequired)),
      designConsiderations: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.designConsiderations)),
      unionProject: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.unionProject)),
      specialTaggingRequirements: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.specialTaggingRequirements)),
      responsibleForHazardousMaterials: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsExecutionSchedule.responsibleForHazardousMaterials)),
      confinedWorkSpace: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsExecutionSchedule.confinedWorkSpace)),
      responsibleForInaccessibleAccess: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsExecutionSchedule.responsibleForInaccessibleAccess)),
    });
  }

  createOutsidePurchasesForm() {
    this.outsidePurchaseFormGroup = this.formBuilder.group({
      specialFabricationItems: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.specialFabricationItems)),
      heatersRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.heatersRequired)),
      thirdPartyMaterialsIdentified: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.thirdPartyMaterialsIdentified)),
      supplierPartNumbers: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.supplierPartNumbers)),
      outsidePurchaseSystems: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.outsidePurchaseSystems)),
      ifYesRequireManufacturer: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewEnUsOutsidePurchase.ifYesRequireManufacturer))
    });
  }

  createWiringResponsibilitiesForm() {
    this.wiringResponsibilitiesFormGroup = this.formBuilder.group({
      fieldTerminations: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.fieldTerminations)),
      panelTerminations: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.panelTerminations)),
      padAndRemoteAnnunciator: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.padAndRemoteAnnunciator)),
      highVoltageWiring: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.highVoltageWiring)),
      supply24Vdc: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsWiringResponsibilities.supply24Vdc)),
      thirdPartyTerminations: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsWiringResponsibilities.thirdPartyTerminations)),
      fiberOpticsRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsWiringResponsibilities.fiberOpticsRequired)),
      fiberFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.fiberFurnishedBy)),
      fiberInstalledBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.fiberInstalledBy)),
      fiberTerminationsBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.fiberTerminationsBy)),
      ductHousingsAndSampling: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.ductHousingsAndSampling)),
      providesPowerForEnclosureHeaters: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.providesPowerForEnclosureHeaters)),
      fireSmokeDamperValve: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.fireSmokeDamperValve)),
      powerForActuatorsFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.powerForActuatorsFurnishedBy)),
      powerWiring: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.powerWiring)),
      wireCablingFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.wireCablingFurnishedBy)),
      freeRunCableAcceptable: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewFireEnUsWiringResponsibilities.freeRunCableAcceptable)),
      lowVoltageWiring: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.lowVoltageWiring)),
      devicesInstalled: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.devicesInstalled)),
      devicesWired: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.devicesWired)),
      equipmentInstalled: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewFireEnUsWiringResponsibilities.equipmentInstalled))
    });
    }

  createManagementStationForm() {
    this.managementStationFormGroup = this.formBuilder.group({
      managementStation: this.formBuilder.control(this.costReviewSecurityEnUsManagementStation.managementStation)
    });
  }

  createElectricalWiringForm() {
    this.electricalWiringFormGroup = this.formBuilder.group({
      electricalSubcontractor: this.formBuilder.control(this.costReviewElectricalWiring.electricalSubcontractor),
      amountBudgeted: this.formBuilder.control(this.costReviewElectricalWiring.amountBudgeted || 0),
      threeBidsSecured: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.threeBidsSecured)),
      vendorNameBidOne: this.formBuilder.control(this.costReviewElectricalWiring.vendorNameBidOne),
      vendorNameBidTwo: this.formBuilder.control(this.costReviewElectricalWiring.vendorNameBidTwo),
      vendorNameBidThree: this.formBuilder.control(this.costReviewElectricalWiring.vendorNameBidThree),
      requestedQuotesFrom: this.formBuilder.control(this.costReviewElectricalWiring.requestedQuotesFrom),
      fpmInvolved: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.fpmInvolved)),
      quoteVariance: this.formBuilder.control(this.costReviewElectricalWiring.quoteVariance || 0),
      receivedBindingQuote: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.receivedBindingQuote)),
      subIsBauCertified: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.subIsBauCertified)),
      securityPMInvolved: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.securityPMInvolved)),
      subHasProjectSpecifications: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.subHasProjectSpecifications)),
      subHasCapacityToCompleteWork: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.subHasCapacityToCompleteWork)),
      subCarriesBond: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.subCarriesBond)),
      subCarriesBondReason: this.formBuilder.control(this.costReviewElectricalWiring.subCarriesBondReason),
      subsNotifiedSecurity: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.subsNotifiedSecurity)),
      contractValue: this.formBuilder.control(this.costReviewElectricalWiring.contractValue || 0),
      allConduitRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.allConduitRequired)),
      conduitType: this.formBuilder.control(this.getParameterValues(this.constants.CONDUIT_TYPE, this.costReviewElectricalWiring.conduitType)),
      fiberOpticsSecurity: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.fiberOpticsSecurity)),
      wiringType: this.formBuilder.control(this.getParameterValues(this.constants.ELECTRICALWIRING_WIRING_TYPE, this.costReviewElectricalWiring.wiringType)),
      minimumConduitSize: this.formBuilder.control(this.getIndexBasedParameterKey(this.minimumConduitSizeOptions, this.costReviewElectricalWiring.minimumConduitSize)),
      safetiesFurnishedBy: this.formBuilder.control(this.costReviewElectricalWiring.safetiesFurnishedBy),
      contractorSubcontractorHasDPU: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.contractorSubcontractorHasDPU)),
      ifNoWillOneBeProvided: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewElectricalWiring.ifNoWillOneBeProvided))
    });
    }

    createWiringInterlockForm() {
    this.wiringInterlockFormGroup = this.formBuilder.group({
      boilerChillerWiringDetailed: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.boilerChillerWiringDetailed)),
      flowSwitchesWiredBy: this.formBuilder.control(this.costReviewWiringInterlock.flowSwitchesWiredBy),
      chemicalFeedSystemWiringRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.chemicalFeedSystemWiringRequired)),
      sumpLevelControllerWiringRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.sumpLevelControllerWiringRequired)),
      other: this.formBuilder.control(this.costReviewWiringInterlock.other),
      splitSystemDxWiringRequired: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.splitSystemDxWiringRequired)),
      exhaustFanWiring: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.exhaustFanWiring)),
      exhaustFanDamperInterlocks: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.exhaustFanDamperInterlocks)),
      coolingTowerWiring: this.formBuilder.control(this.costReviewWiringInterlock.coolingTowerWiring),
      panelPowerWiredBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewWiringInterlock.panelPowerWiredBy)),
      smokeDetectorsInstalledBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewWiringInterlock.smokeDetectorsInstalledBy)),
      smokeDetectorsFurnishedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewWiringInterlock.smokeDetectorsFurnishedBy)),
      interlocks120Volt: this.formBuilder.control(this.getParameterValues(this.constants.YES_NO, this.costReviewWiringInterlock.interlocks120Volt)),
      smokeDetectorsLocatedBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewWiringInterlock.smokeDetectorsLocatedBy)),
      siemensPanelsPoweredBy: this.formBuilder.control(this.getParameterValues(this.constants.SIEMENS_OTHER, this.costReviewWiringInterlock.siemensPanelsPoweredBy))
    });
    }

  getParameterValues(key: string, value: string | number | undefined) {
    value ??= Constants.EMPTY_STRING;

    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  getParameterValuesForArray(key: string, value: Array<string | number> | undefined) {
    if (!value || value.length === 0) {
      value = [Constants.EMPTY_STRING];
    }
    return this.reviewService.getCostReviewParametersDescriptionForArray(key, value);
  }

  getDropdownValues(key: string): { label: string; value: string }[] {
    return this.reviewService.getDropdownValues(key);
  }

  isIntegrationProtocolSelected(option: number): boolean {
    const integrationProtocol = this.integrationFormGroup?.controls.integrationProtocol?.value || [];
    return integrationProtocol.includes(option);
  }

  isSystemInterfacesSelected(option: number): boolean {
    const selectedOptions = this.systemInterfacesFormGroup?.controls.systemInterfaces?.value;
    return Array.isArray(selectedOptions) && selectedOptions.some((value) => Number(value) === option);
  }

  isFireSmokeDampersSelected(option: number): boolean {
    const selectedOptions = this.systemInterfacesFormGroup?.controls.fireSmokeDampers?.value;
    return Array.isArray(selectedOptions) && selectedOptions.some((value) => Number(value) === option);
  }

  isLedAnnunciatorSelected(option: number): boolean {
    const selectedOptions = this.systemInterfacesFormGroup?.controls.ledAnnunciator?.value;
    return Array.isArray(selectedOptions) && selectedOptions.some((value) => Number(value) === option);
  }

  isAlphaNumericAnnunciatorSelected(option: number): boolean {
    const selectedOptions = this.systemInterfacesFormGroup?.controls.alphaNumericAnnunciator?.value;
    return Array.isArray(selectedOptions) && selectedOptions.some((value) => Number(value) === option);
  }

  private refreshSystemInterfacesDisabledState(): void {
    this.isFireSmokeDampersDisabled = !this.isSystemInterfacesSelected(8);
    this.isLedAnnunciatorDisabled = !this.isSystemInterfacesSelected(14);
    this.isAlphaNumericAnnunciatorDisabled = !this.isSystemInterfacesSelected(15);
    this.isOtherNotesDisabled = !this.isSystemInterfacesSelected(17);
  }

  formControlsValueForString(formGroup: FormGroup | undefined, formControlName: string): string {
    return this.commonFunctionsService.formControlsValueForString(formGroup, formControlName);
  }

  isControlValueEqualToTranslation(formGroup: FormGroup | undefined, formControlName: string, translationKey: string): boolean {
    return this.commonFunctionsService.isControlValueEqualToTranslation(formGroup, formControlName, translationKey);
  }

  isValueEqualToTranslation(value: string | null | undefined, translationKey: string): boolean {
    return this.commonFunctionsService.isValueEqualToTranslation(value, translationKey);
  }

  formControlsValueForNumber(formGroupName: FormGroup, formControlName: string): number {
    const controlValue: number = formGroupName?.controls[formControlName]?.value;
    return controlValue || 0;  
  }

  updateNumberArrayControl(formGroup: FormGroup | undefined, controlName: string, option: number, checked: boolean): void {
    const control = formGroup?.get(controlName);
    if (!control) {
      return;
    }

    const currentValues = Array.isArray(control.value) ? [...control.value] as number[] : [];
    const nextValues = checked
      ? Array.from(new Set([...currentValues, option]))
      : currentValues.filter((value) => Number(value) !== Number(option));

    control.setValue(nextValues);
    if (formGroup === this.systemInterfacesFormGroup) {
      this.refreshSystemInterfacesDisabledState();
    }
  }

  setFormControlValue(formGroup: FormGroup | undefined, controlName: string, value: string | number): void {
    const control = formGroup?.get(controlName);
    if (!control || control.value === value) {
      return;
    }

    control.setValue(value);
  }

  private getParameterCode(parameter: string, description: string, currentValue: number | null | undefined): number {
    const code = this.reviewService.getCostReviewParametersCode(parameter, description);
    return code !== undefined ? code : (currentValue ?? 0);
  }

  private getIndexBasedParameterKey(options: string[], code: number | undefined): string {
    if (!code || code < 1 || code > options.length) {
      return Constants.EMPTY_STRING;
    }

    return options[code - 1];
  }

  private getIndexBasedParameterCode(options: string[], value: string | undefined, currentValue: number | null | undefined): number {
    if (!value) {
      return currentValue ?? 0;
    }

    const optionIndex = options.findIndex((option) => option === value);
    return optionIndex >= 0 ? optionIndex + 1 : (currentValue ?? 0);
  }

  private toNumericArray(values: Array<string | number> | undefined): number[] {
    if (!Array.isArray(values)) {
      return [];
    }

    return values
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));
  }

  private getCodesForArray(parameter: string, value: string | Array<string | number> | undefined): number[] {
    const values = Array.isArray(value)
      ? value
      : (value ?? Constants.EMPTY_STRING)
        .split(', ')
        .filter((item) => item.length > 0);
    const options = this.reviewService.costReviewParameters
      .filter((item) => item.parameterID.startsWith(`${parameter}_`))
      .sort((first, second) => first.parameterID.localeCompare(second.parameterID, undefined, { numeric: true }))
      .map((item) => item.description);

    return this.commonFunctionsService.toNumberArray(values, options);
  }

   private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }

}
